import { useState } from 'react';
import { MoreHorizontal, Archive, ArchiveRestore, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { qualityAPI, NonConformanceReport } from '../../lib/api';
import { getErrorMessage } from '../../hooks/useFetchData';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog';

interface NCRRowActionsProps {
  ncr: NonConformanceReport;
  /** Refetch the register after an action changes what belongs in the list. */
  onChanged: () => void;
}

/**
 * Per-row admin actions on the NCR register: archive / unarchive, and delete
 * for manually-raised NCRs.
 *
 * Only rendered for customer admins — Quality.tsx omits the whole column
 * otherwise, and every action is re-checked server-side, so hiding the menu is
 * presentation rather than the permission itself.
 *
 * Delete is deliberately absent on imported NCRs: the ERP still owns those and
 * the next import recreates them, so the row would vanish from history for no
 * lasting effect. The menu says why rather than offering an action that 403s.
 */
export default function NCRRowActions({ ncr, onChanged }: NCRRowActionsProps) {
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const archived = Boolean(ncr.is_archived ?? ncr.archived_at);
  const canDelete = Boolean(ncr.created_manually);

  const run = async (
    fn: () => Promise<unknown>,
    successMessage: string,
  ) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMessage);
      onChanged();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const label = ncr.ncr_number || `NCR ${ncr.id}`;

  return (
    // Row click opens the NCR — the menu must not navigate underneath itself.
    <div onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            disabled={busy}
            aria-label={`Actions for ${label}`}
          >
            {busy
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <MoreHorizontal className="h-4 w-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {archived ? (
            <DropdownMenuItem
              onSelect={() => run(
                () => qualityAPI.unarchiveNCR(ncr.id),
                `${label} restored to the register`,
              )}
            >
              <ArchiveRestore className="mr-2 h-4 w-4" />
              Unarchive
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onSelect={() => run(
                () => qualityAPI.archiveNCR(ncr.id),
                `${label} archived`,
              )}
            >
              <Archive className="mr-2 h-4 w-4" />
              Archive
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {canDelete ? (
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600"
              onSelect={(e) => {
                // Radix closes the menu on select; deferring the dialog to the
                // next tick stops the two unmount/mount cycles colliding and
                // leaving the page with a stuck pointer-events:none body.
                e.preventDefault();
                setTimeout(() => setConfirmDelete(true), 0);
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          ) : (
            <div className="px-2 py-1.5 text-xs text-[#7e8299]">
              Imported NCRs can&apos;t be deleted — archive instead.
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the NCR and its corrective actions,
              documents and history. It cannot be undone. To keep the record but
              take it off the register, archive it instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                run(
                  () => qualityAPI.deleteNCR(ncr.id),
                  `${label} deleted`,
                ).then(() => setConfirmDelete(false));
              }}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete NCR
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
